import type { ElementType, HTMLAttributes, ReactNode } from "react";
import { cn } from "~/foundation/utils/cn";

type Variant = "body" | "secondary" | "tertiary" | "small" | "h1" | "h2" | "h3";

export interface TextProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
  variant?: Variant;
  weight?: "regular" | "medium" | "bold";
  truncate?: boolean;
  children?: ReactNode;
}

const VARIANT: Record<Variant, string> = {
  body: "text-body text-text",
  secondary: "text-body text-text-secondary",
  tertiary: "text-body text-text-tertiary",
  small: "text-small text-text-secondary",
  h1: "text-h1 text-text font-semibold leading-tight",
  h2: "text-h2 text-text font-semibold leading-tight",
  h3: "text-h3 text-text font-semibold leading-tight",
};

const WEIGHT = {
  regular: "font-normal",
  medium: "font-medium",
  bold: "font-semibold",
} as const;

const DEFAULT_ELEMENT: Record<Variant, ElementType> = {
  body: "p",
  secondary: "p",
  tertiary: "p",
  small: "p",
  h1: "h1",
  h2: "h2",
  h3: "h3",
};

export function Text({
  as,
  variant = "body",
  weight,
  truncate,
  className,
  children,
  ...rest
}: TextProps) {
  const Component = as ?? DEFAULT_ELEMENT[variant];
  return (
    <Component
      className={cn(VARIANT[variant], weight && WEIGHT[weight], truncate && "truncate", className)}
      {...rest}
    >
      {children}
    </Component>
  );
}
